import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import lodash from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllRoles } from "src/clients/RolesAndPermissionsService";
import { selectUser } from "src/redux/slices/userSlice";
import { UserAppCredentials, UserDetails } from "src/types/ClientDetails";
import { Role } from "src/types/RolesAndPermissions";
import { useClientDataContext } from "../context/Context";
import WarningPopup from "../modal/WarningPopup";
import NotifyError from "../notifications/NotifyError";
import createUserStyles from "./CreateUser.module.css";

interface createUserProps {
  index: number;
  mode: string;
  handleOnCreateUserCancel: () => void;
  handleOnSuccessBack: () => void;
}
const CreateUser = ({
  index,
  mode,
  handleOnCreateUserCancel,
  handleOnSuccessBack,
}: createUserProps) => {
  const { clientData, setClientData } = useClientDataContext();
  const user = useSelector(selectUser);
  const initialUserDetails =
    index !== -1
      ? clientData.userDetailsList[index]
      : {
          firstName: "",
          lastName: "",
          userId: "",
          password: "",
          email: "",
          role:
            user.role === "SYSTEM_ADMIN"
              ? clientData.clientId === 1
                ? { roleId: 2, roleName: "SYSTEM_USER" }
                : { roleId: 3, roleName: "ADMIN" }
              : { roleId: 4, roleName: "USER" },

          userAppCredentials: [],
          status: "INACTIVE",
          onBoarded: false,
          defaultProduct: "",
        };
  const [currentUserDetails, setCurrentUserDetails] = useState<UserDetails>({
    ...initialUserDetails,
    role: { ...initialUserDetails.role },
    userAppCredentials: initialUserDetails.userAppCredentials.map(
      (userAppCredential) => ({ ...userAppCredential })
    ),
  });
  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [roleError, setRoleError] = useState<boolean>(false);
  const [productsError, setProductsError] = useState<boolean>(false);
  const [isDiscardPopUpOpen, setIsDiscardPopUpOpen] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [roleList, setRoleList] = useState<Role[]>([]);
  const statusList = ["ACTIVE", "INACTIVE"];
  const isUserEdit =
    user.permissionsList.includes("EDIT USER") &&
    user.role !== currentUserDetails.role.roleName;

  let productsList: UserAppCredentials[] = clientData.appCredentialsList.map(
    (appCredential) => ({
      prdUsername: appCredential.prdUsername,
      prdPassword: appCredential.prdPassword,
      onBoarded: false,
      products: appCredential.products,
    })
  );

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (index !== -1) {
      setCurrentUserDetails((prev) => ({
        ...prev,
        ...initialUserDetails,
        role: { ...initialUserDetails.role },
        userAppCredentials: initialUserDetails.userAppCredentials.map(
          (userAppCredential) => ({ ...userAppCredential })
        ),
      }));
    }
  }, [clientData.userDetailsList]);

  useEffect(() => {
    if (firstNameError === true) {
      setFirstNameError(false);
    }
    if (lastNameError === true) {
      setLastNameError(false);
    }
    if (emailError === true) {
      setEmailError(false);
    }
    if (roleError === true) {
      setRoleError(false);
    }
    if (productsError === true) {
      setProductsError(false);
    }
  }, [
    currentUserDetails.firstName,
    currentUserDetails.lastName,
    currentUserDetails.email,
    currentUserDetails.role,
    currentUserDetails.userAppCredentials,
  ]);

  const fetchRoles = () => {
    getAllRoles(user.userId, user.password).then(async (res) => {
      if (res.ok) {
        const rolesData = await res.json();
        let filteredData;
        if (user.role === "SYSTEM_ADMIN") {
          filteredData = rolesData.filter(
            (roleData: Role) => roleData.roleName !== "SYSTEM_ADMIN"
          );
          setRoleList(filteredData);
        } else if (user.role === "ADMIN") {
          filteredData = rolesData.filter(
            (roleData: Role) =>
              roleData.roleName !== "SYSTEM_ADMIN" &&
              roleData.roleName !== "SYSTEM_USER" &&
              roleData.roleName !== "ADMIN"
          );
          setRoleList(filteredData);
        } else {
          setRoleList([]);
        }
      } else {
        toast.error(
          <NotifyError header={"Error"}>
            <Typography variant="body2">Failed to fetch Roles</Typography>
          </NotifyError>,
          {
            icon: false,
            closeButton: false,
          }
        );
      }
    });
  };

  const isEmail = (email: string) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z.-]+\.[a-zA-Z]{2,6}$/.test(email);
  };

  const validateUserFields = () => {
    let userIdExp = "^[a-zA-Z ]{3,}$";
    if (
      !currentUserDetails.firstName.match(userIdExp) ||
      !currentUserDetails.lastName.match(userIdExp)
    ) {
      if (!currentUserDetails.firstName.match(userIdExp)) {
        setFirstNameError(true);
      }
      if (!currentUserDetails.lastName.match(userIdExp)) {
        setLastNameError(true);
      }
    } else if (!isEmail(currentUserDetails.email)) {
      setEmailError(true);
    } else if (
      currentUserDetails.role.roleId === 0 ||
      currentUserDetails.userAppCredentials.length === 0
    ) {
      if (currentUserDetails.role.roleId === 0) {
        setRoleError(true);
      }
      if (currentUserDetails.userAppCredentials.length === 0) {
        setProductsError(true);
      }
    } else {
      currentUserDetails.userId =
        currentUserDetails.userId !== ""
          ? currentUserDetails.userId
          : currentUserDetails.email.split("@")[0];
      currentUserDetails.defaultProduct === "" &&
        (currentUserDetails.defaultProduct =
          currentUserDetails.userAppCredentials[0].products.productName);
      saveUser(currentUserDetails);
      handleOnSuccessBack();
    }
  };

  const handleOnRoleChange = (role: Role) => {
    setCurrentUserDetails((prevState) => ({
      ...prevState,
      role:
        prevState.role.roleId === role.roleId
          ? { roleId: 0, roleName: "" }
          : role,
    }));
  };

  const handleOnDefaultProductChange = (defaultProduct: string) => {
    setCurrentUserDetails((prevState) => ({
      ...prevState,
      defaultProduct:
        prevState.defaultProduct === defaultProduct ? "" : defaultProduct,
    }));
  };

  const handleOnStatusChange = (status: string) => {
    setCurrentUserDetails((prevState) => ({
      ...prevState,
      status: status,
    }));
  };

  const handleOnProductsChange = (userAppCred: UserAppCredentials) => {
    setCurrentUserDetails((prevState) => {
      const updatedCurrentUserDetails = { ...prevState };
      const updatedUserAppCredentials = [
        ...updatedCurrentUserDetails.userAppCredentials,
      ];
      const isProductInList = updatedUserAppCredentials.some(
        (userAppCredential) => {
          return (
            userAppCredential.products?.productId ===
            userAppCred.products?.productId
          );
        }
      );

      if (isProductInList) {
        const index = updatedUserAppCredentials.findIndex(
          (userAppCredential) =>
            userAppCredential.products?.productId ===
            userAppCred.products?.productId
        );
        updatedUserAppCredentials.splice(index, 1);
      } else {
        updatedUserAppCredentials.push(userAppCred);
      }
      updatedCurrentUserDetails.userAppCredentials = updatedUserAppCredentials;

      if (updatedUserAppCredentials.length > 0) {
        updatedCurrentUserDetails.defaultProduct =
          updatedUserAppCredentials[0].products?.productName;
      } else {
        updatedCurrentUserDetails.defaultProduct = "";
      }
      return updatedCurrentUserDetails;
    });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCurrentUserDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // const handleAppCredentialsInputChange = (
  //   event: ChangeEvent<HTMLInputElement>
  // ) => {
  //   const { name, value } = event.target;
  //   const [, index, field] = name.split(".");
  //   setCurrentUserDetails((prevState) => {
  //     const userAppCredentialsList = [...prevState.userAppCredentials];
  //     const userAppCredential = {
  //       ...userAppCredentialsList[parseInt(index, 10)],
  //       [field]: value,
  //     };
  //     userAppCredentialsList[parseInt(index, 10)] = userAppCredential;
  //     return { ...prevState, userAppCredentials: userAppCredentialsList };
  //   });
  // };

  // const handleTogglePasswordVisibility = (index: number) => {
  //   setShowPassword((prevShowPassword) => !prevShowPassword);
  // };

  const saveUser = (newUserDetails: UserDetails) => {
    setClientData((prevState) => {
      const updatedClientData = { ...prevState };
      const updatedUserDetailsList = [...updatedClientData.userDetailsList];
      if (index !== -1) {
        const updatedUser = {
          ...updatedUserDetailsList[index],
          firstName: newUserDetails.firstName,
          lastName: newUserDetails.lastName,
          userId: newUserDetails.userId,
          email: newUserDetails.email,
          role: newUserDetails.role,
          userAppCredentials: [...newUserDetails.userAppCredentials],
          defaultProduct: newUserDetails.defaultProduct,
          status: newUserDetails.status,
        };
        updatedUserDetailsList[index] = updatedUser;
      } else {
        updatedUserDetailsList.push(newUserDetails);
      }
      updatedClientData.userDetailsList = updatedUserDetailsList;
      return updatedClientData;
    });
  };

  const handleOnSave = () => {
    validateUserFields();
  };

  const handleOnCancel = () => {
    if (!lodash.isEqual(initialUserDetails, currentUserDetails)) {
      setIsDiscardPopUpOpen(true);
    } else {
      handleOnCreateUserCancel();
    }
  };

  const handleOnPopUpSave = () => {
    setIsDiscardPopUpOpen(false);
    handleOnSave();
  };

  const handleOnPopUpDiscard = () => {
    setIsDiscardPopUpOpen(false);
    if (mode === "UserEdit") {
      setCurrentUserDetails(initialUserDetails);
    }
    handleOnCreateUserCancel();
  };

  const handleOnWarningPopUpClose = () => {
    setIsDiscardPopUpOpen(false);
  };

  return (
    <>
      <Box className={createUserStyles.rootContainer}>
        <Box>
          <Typography
            variant="h5"
            sx={{ padding: "2%" }}
          >
            {mode === "Edit" || mode === "UserEdit"
              ? "Edit "
              : mode === "View"
                ? "View "
                : "Create "}
            User
          </Typography>
        </Box>
        <Box sx={{ padding: "0rem 2rem 1rem 2rem" }}>
          <Stack
            direction="column"
            spacing={2}
          >
            <Stack
              direction="row"
              spacing={10}
            >
              <Box className={createUserStyles.stackBox}>
                <Typography
                  variant="body1"
                  className={createUserStyles.typographyBox}
                >
                  First Name
                  <span
                    style={{
                      color: "#f66",
                    }}
                  >
                    *
                  </span>
                </Typography>
                <TextField
                  type="text"
                  size="small"
                  placeholder="First Name"
                  fullWidth
                  required
                  name="firstName"
                  value={currentUserDetails.firstName}
                  onChange={handleInputChange}
                  helperText={firstNameError ? "Incorrect First Name" : ""}
                  InputProps={{
                    readOnly: !isUserEdit,
                  }}
                />
              </Box>
              <Box className={createUserStyles.stackBox}>
                <Typography
                  variant="body1"
                  className={createUserStyles.typographyBox}
                >
                  Last Name
                  <span
                    style={{
                      color: "#f66",
                    }}
                  >
                    *
                  </span>
                </Typography>
                <TextField
                  type="text"
                  size="small"
                  placeholder="Last Name"
                  fullWidth
                  name="lastName"
                  value={currentUserDetails.lastName}
                  onChange={handleInputChange}
                  helperText={lastNameError ? "Incorrect Last Name" : ""}
                  InputProps={{
                    readOnly: !isUserEdit,
                  }}
                />
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={10}
            >
              <Box className={createUserStyles.stackBox}>
                <Typography
                  variant="body1"
                  className={createUserStyles.typographyBox}
                >
                  Email
                  <span
                    style={{
                      color: "#f66",
                    }}
                  >
                    *
                  </span>
                </Typography>
                <TextField
                  type="email"
                  size="small"
                  placeholder="Email"
                  variant="outlined"
                  fullWidth
                  disabled={currentUserDetails.id !== undefined}
                  name="email"
                  value={currentUserDetails.email}
                  onChange={handleInputChange}
                  helperText={emailError ? "Invalid Email" : ""}
                />
              </Box>
              <Box className={createUserStyles.stackBox}>
                <Typography
                  variant="body1"
                  className={createUserStyles.typographyBox}
                >
                  userId
                </Typography>
                <TextField
                  type="text"
                  size="small"
                  placeholder="userId"
                  fullWidth
                  name="userId"
                  value={
                    currentUserDetails.userId
                      ? currentUserDetails.userId
                      : currentUserDetails.email.split("@")[0]
                  }
                  onChange={handleInputChange}
                  disabled={currentUserDetails.id !== undefined}
                />
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={10}
            >
              <Box className={createUserStyles.stackBox}>
                <Typography
                  variant="body1"
                  className={createUserStyles.typographyBox}
                >
                  Role
                  <span
                    style={{
                      color: "#f66",
                    }}
                  >
                    *
                  </span>
                </Typography>
                <FormControl
                  fullWidth
                  sx={{ alignSelf: "center" }}
                >
                  <Select
                    value={currentUserDetails.role.roleName}
                    size="small"
                    displayEmpty
                    disabled={mode === "UserEdit"}
                    readOnly={!isUserEdit}
                    renderValue={(value) =>
                      value !== "" ? (
                        value
                      ) : (
                        <span style={{ color: "#aaa" }}> Role</span>
                      )
                    }
                  >
                    {roleList
                      .filter((role) => {
                        if (clientData.clientId === 1) {
                          return role.roleName === "SYSTEM_USER";
                        }
                        return (
                          role.roleName === "ADMIN" || role.roleName === "USER"
                        );
                      })
                      .map((role) => (
                        <MenuItem
                          key={role.roleId}
                          value={role.roleName}
                          onClick={() => handleOnRoleChange(role)}
                        >
                          {role.roleName}
                        </MenuItem>
                      ))}
                  </Select>
                  {roleError && (
                    <FormHelperText>Please Select Role</FormHelperText>
                  )}
                </FormControl>
              </Box>
              <Box className={createUserStyles.stackBox}>
                <Typography
                  variant="body1"
                  className={createUserStyles.typographyBox}
                >
                  Products
                  <span
                    style={{
                      color: "#f66",
                    }}
                  >
                    *
                  </span>
                </Typography>
                <FormControl
                  fullWidth
                  sx={{ alignSelf: "center" }}
                >
                  <Select
                    displayEmpty
                    size="small"
                    multiple
                    disabled={mode === "UserEdit"}
                    readOnly={!isUserEdit}
                    value={currentUserDetails.userAppCredentials.map(
                      (userAppCredential) =>
                        userAppCredential.products?.productName
                    )}
                    renderValue={(selectedValues) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedValues.length !== 0 ? (
                          selectedValues.map((value: string) => (
                            <Chip
                              key={value}
                              label={value}
                            />
                          ))
                        ) : (
                          <span style={{ color: "#aaa" }}> Products</span>
                        )}
                      </Box>
                    )}
                  >
                    {productsList.map((userAppCredential) => (
                      <MenuItem
                        key={userAppCredential.products?.productId}
                        value={userAppCredential.products?.productName}
                        onClick={() =>
                          handleOnProductsChange(userAppCredential)
                        }
                      >
                        {userAppCredential.products?.productName}
                      </MenuItem>
                    ))}
                  </Select>
                  {productsError && (
                    <FormHelperText>
                      Please Select atleast one product
                    </FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={10}
            >
              <Box className={createUserStyles.stackBox}>
                <Typography
                  variant="body1"
                  className={createUserStyles.typographyBox}
                >
                  Default Product
                </Typography>
                <FormControl
                  fullWidth
                  sx={{ alignSelf: "center" }}
                >
                  <Select
                    value={currentUserDetails.defaultProduct}
                    size="small"
                    displayEmpty
                    disabled={mode === "UserEdit"}
                    readOnly={!isUserEdit}
                    renderValue={(value) =>
                      value !== "" ? (
                        value
                      ) : (
                        <span style={{ color: "#aaa" }}> Default Product</span>
                      )
                    }
                  >
                    {currentUserDetails.userAppCredentials.length !== 0 ? (
                      currentUserDetails.userAppCredentials.map(
                        (userAppCredential) => (
                          <MenuItem
                            key={userAppCredential.products?.productId}
                            value={userAppCredential.products?.productName}
                            onClick={() =>
                              handleOnDefaultProductChange(
                                userAppCredential.products.productName
                              )
                            }
                          >
                            {userAppCredential.products?.productName}
                          </MenuItem>
                        )
                      )
                    ) : (
                      <MenuItem value={"Please Select Products"}>
                        {"Please Select Products"}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box className={createUserStyles.stackBox}>
                {mode !== "UserEdit" ? (
                  <>
                    <Typography
                      variant="body1"
                      className={createUserStyles.typographyBox}
                    >
                      Status
                    </Typography>
                    <FormControl
                      fullWidth
                      sx={{ alignSelf: "center" }}
                    >
                      <Select
                        value={currentUserDetails.status}
                        size="small"
                        displayEmpty
                        disabled={
                          !currentUserDetails.onBoarded ||
                          mode === "UserEdit" ||
                          productsList.every((product) => {
                            const correspondingAppCredential =
                              clientData.appCredentialsList.find(
                                (appCred) =>
                                  appCred.products.productId ===
                                  product.products.productId
                              );
                            return correspondingAppCredential
                              ? !correspondingAppCredential.isActive
                              : true;
                          })
                        }
                        readOnly={
                          !isUserEdit ||
                          !user.permissionsList.includes("ACTIVE/INACTIVE USER")
                        }
                        renderValue={(value) =>
                          value !== "" ? (
                            value
                          ) : (
                            <span style={{ color: "#aaa" }}> Status</span>
                          )
                        }
                      >
                        {statusList.map((status, index) => (
                          <MenuItem
                            key={index}
                            value={status}
                            onClick={() => handleOnStatusChange(status)}
                          >
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                ) : (
                  <></>
                )}
              </Box>
            </Stack>

            {/* <Box sx={{ maxHeight: "20vh", overflowY: "scroll" }}>
              {currentUserDetails.userAppCredentials.map(
                (userAppCredential, index) => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      margin: "1rem 0rem",
                    }}
                    key={index}
                  >
                    <Accordion sx={{ width: "100%" }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          variant="body1"
                          className={createUserStyles.typographyBox}
                        >
                          {userAppCredential.products?.productName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack
                          direction={"column"}
                          spacing={2}
                        >
                          <Stack
                            direction="row"
                            spacing={10}
                          >
                            <Box className={createUserStyles.stackBox}>
                              <Typography
                                variant="body1"
                                className={createUserStyles.typographyBox}
                              >
                                User Name
                              </Typography>
                              <TextField
                                type="text"
                                size="small"
                                placeholder="User Name"
                                fullWidth
                                name={`appCredentialsList.${index}.username`}
                                autoComplete="off"
                                value={userAppCredential.username}
                                onChange={handleAppCredentialsInputChange}
                              />
                            </Box>
                            <Box className={createUserStyles.stackBox}>
                              <Typography
                                variant="body1"
                                className={createUserStyles.typographyBox}
                              >
                                Password
                              </Typography>
                              <TextField
                                type={showPassword ? "text" : "password"}
                                size="small"
                                placeholder="Password"
                                fullWidth
                                name={`appCredentialsList.${index}.password`}
                                autoComplete="new-password"
                                value={userAppCredential.password}
                                onChange={handleAppCredentialsInputChange}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() =>
                                          handleTogglePasswordVisibility(index)
                                        }
                                        edge="end"
                                      >
                                        {showPassword ? (
                                          <Visibility />
                                        ) : (
                                          <VisibilityOff />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Box>
                          </Stack>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )
              )}
            </Box> */}
          </Stack>
        </Box>
      </Box>
      <Box className={createUserStyles.actionBox}>
        <Box className={createUserStyles.actionButtonsBox}>
          <Button
            variant="outlined"
            onClick={handleOnSave}
            disabled={lodash.isEqual(initialUserDetails, currentUserDetails)}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={handleOnCancel}
          >
            Cancel
          </Button>
        </Box>
      </Box>
      {isDiscardPopUpOpen && (
        <WarningPopup
          isOpen={true}
          onClose={handleOnWarningPopUpClose}
          actionButtons={[
            {
              name: "Save",
              onButtonClick: handleOnPopUpSave,
            },
            {
              name: "Discard",
              onButtonClick: handleOnPopUpDiscard,
            },
          ]}
        >
          <Box className={createUserStyles.warningPopUpBox}>
            <WarningIcon />
            <Typography variant="body2">
              There are changes in the data do you want to save it.
            </Typography>
          </Box>
        </WarningPopup>
      )}
    </>
  );
};

export default CreateUser;
